import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  adminFetchBtwApplications,
  adminArchiveBtwApplication,
  adminDeleteBtwApplication,
  adminFetchBtwStatuses,
  adminFetchAdmins,
  adminFetchBtwLabels,
  adminUpdateBtwWorkflowStatus,
  fetchDashboardData,
} from "../../core/axios/endpoints";
import { useUser } from "../../core/context/user";
import ConfirmModal from "../../components/confirm-modal";
import PartnerInvoiceBuilderModal from "../../components/partner-invoice-builder";
import BtwAssigneeAvatar from "../../components/btw-assignee-avatar";
import BtwLabelPill from "../../components/btw-label-pill";
import BtwFilterBar, { useBtwFilters } from "../../components/btw-filter-bar";
import { daysSince, getStatusColor } from "../../core/btw-status-colors";
import { saveBtwLastUrl } from "../../core/btw-last-url";
import Papa from "papaparse";

const STATUS_LABELS = {
  pending_payment: { label: "Wacht op betaling", color: "bg-amber-100 text-amber-800" },
  paid: { label: "Betaald", color: "bg-emerald-100 text-emerald-800" },
  abandoned: { label: "Afgebroken", color: "bg-gray-100 text-gray-700" },
  refunded: { label: "Terugbetaald", color: "bg-rose-100 text-rose-800" },
};

// Partner-mode: één pill die de factuur-status laat zien. Dit is de
// "is er geld binnen"-indicator voor partner-aanvragen.
const PARTNER_INVOICE_LABELS = {
  pending: { label: "Te factureren", color: "bg-violet-100 text-violet-800" },
  invoiced: { label: "Factuur verstuurd", color: "bg-sky-100 text-sky-800" },
  paid: { label: "Betaald", color: "bg-emerald-100 text-emerald-800" },
  credited: { label: "Gecrediteerd", color: "bg-rose-100 text-rose-800" },
};

// Kwalificatie alleen tonen als die aandacht vergt. "Geschikt" is de
// standaard. "Bel klant" was niet actionable voor admin (geen call-to-action,
// vooral test-noise) en is daarom uit de UI gehaald - het veld blijft wel
// opgeslagen voor de landing-test escalatie-flow.
const QUAL_LABELS = {
  disqualified: { label: "Handmatige beoordeling", color: "bg-amber-100 text-amber-800" },
  incomplete: { label: "Incompleet", color: "bg-gray-100 text-gray-700" },
};

const Badge = ({ map, value }) => {
  const cfg = map[value] ?? { label: value || "-", color: "bg-gray-100 text-gray-700" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${cfg.color}`}
    >
      {cfg.label}
    </span>
  );
};

const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const fmtEuro = (n) =>
  `€ ${(n ?? 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Row = ({ row, selected, onToggle }) => {
  const navigate = useNavigate();
  const s1 = row.step1 ?? {};
  const naam = `${s1.voorletters ?? ""} ${s1.tussenvoegsel ?? ""} ${s1.achternaam ?? ""}`
    .replace(/\s+/g, " ")
    .trim();
  const ws = row.workflowStatusId ?? null;
  const ag = row.assignedToAdminId ?? null;
  const stillDays = daysSince(row.lastActivityAt ?? row.createdAt);
  // Archief is "eruit": geen vastgelopen-signaal en geen deadline-chip meer.
  const isStuck =
    !row.isArchived && stillDays != null && stillDays >= 7 && ws && !ws.isTerminal;
  const edgeColor = ws ? getStatusColor(ws.color).edge : "bg-gray-300";

  const open = (e) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      window.open(
        `/panel/admin/btw-batterij-overzicht/${row._id}`,
        "_blank",
      );
      return;
    }
    navigate(`/panel/admin/btw-batterij-overzicht/${row._id}`, {
      state: { from: "list" },
    });
  };

  const stop = (e) => e.stopPropagation();

  return (
    <tr
      onClick={open}
      className={`group cursor-pointer transition ${
        selected ? "bg-emerald-50/60" : "hover:bg-gray-50"
      }`}
    >
      <td className="px-4 py-2.5" onClick={stop}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(row._id)}
          onClick={stop}
          aria-label={`Selecteer ${naam}`}
          className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300"
        />
      </td>
      <td className="w-1 p-0">
        <div
          className={`w-0.5 ${isStuck ? "bg-rose-400" : edgeColor}`}
          style={{ minHeight: 40 }}
        />
      </td>
      <td className="px-4 py-2.5">
        <div className="font-medium text-gray-900 group-hover:text-emerald-700">
          {naam || "-"}
        </div>
        <div className="truncate text-xs text-gray-500">{s1.email ?? ""}</div>
      </td>
      <td className="px-4 py-2.5">
        {ag ? (
          <div className="flex items-center gap-2">
            <BtwAssigneeAvatar user={ag} size="xs" />
            <span className="truncate text-sm text-gray-700">{ag.naam}</span>
          </div>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-1">
          {row.partnerUserId ? (
            <span
              className="inline-flex items-center gap-1"
              title={`Partner: ${row.partnerUserId?.bedrijfsnaam ?? row.partnerUserId?.naam ?? ""}${
                row.partnerInvoice?.invoiceId?.invoiceNumber
                  ? ` · Factuur ${row.partnerInvoice.invoiceId.invoiceNumber}`
                  : ""
              }`}
            >
              {/* Factuurnummer bewust niet als chip: te druk in de
                  statuskolom. Staat in de tooltip en in de CSV-export. */}
              <Badge
                map={PARTNER_INVOICE_LABELS}
                value={row.partnerInvoice?.status ?? "pending"}
              />
            </span>
          ) : (
            <Badge map={STATUS_LABELS} value={row.status} />
          )}
          {QUAL_LABELS[row.qualificationStatus] ? (
            <Badge map={QUAL_LABELS} value={row.qualificationStatus} />
          ) : null}
          {row.deadlineAt && !row.isArchived ? (() => {
            const days = Math.round(
              (new Date(row.deadlineAt).setHours(0, 0, 0, 0) -
                new Date().setHours(0, 0, 0, 0)) /
                (1000 * 60 * 60 * 24),
            );
            const label =
              days === 0
                ? "deadline vandaag"
                : days === 1
                  ? "deadline morgen"
                  : days > 0
                    ? `deadline over ${days}d`
                    : `${-days}d verlopen`;
            const tone =
              days < 0
                ? "bg-rose-100 text-rose-800"
                : days <= 3
                  ? "bg-amber-100 text-amber-800"
                  : days <= 7
                    ? "bg-sky-100 text-sky-800"
                    : "bg-emerald-100 text-emerald-800";
            return (
              <span
                title={`Deadline: ${new Date(row.deadlineAt).toLocaleDateString("nl-NL")}${row.deadlineNote ? ` - ${row.deadlineNote}` : ""}`}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
              >
                <svg className="h-2.5 w-2.5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-12.25a.75.75 0 00-1.5 0V10c0 .2.08.39.22.53l2.25 2.25a.75.75 0 101.06-1.06L10.75 9.7V5.75z"
                    clipRule="evenodd"
                  />
                </svg>
                {label}
              </span>
            );
          })() : null}
          {Array.isArray(row.labelIds)
            ? row.labelIds.map((lab) =>
                lab && typeof lab === "object" && lab.name ? (
                  <BtwLabelPill key={lab._id ?? lab.name} label={lab} size="sm" />
                ) : null,
              )
            : null}
        </div>
      </td>
      <td className="px-4 py-2.5 text-right font-medium text-gray-900 tabular-nums">
        {(() => {
          // Customer-mode: gebruik het bedrag dat de klant heeft betaald.
          // Partner-mode: klant betaalde 0; toon partner-prijs (per-aanvraag-fee
          // van die partner) zodat de admin de echte omzet-waarde ziet.
          const isPartner = !!row.partnerUserId;
          if (!isPartner) return fmtEuro(row.amountInclVat);
          const partner = row.partnerUserId;
          const excl =
            partner?.applicationFees?.btwBatterij ??
            partner?.btwPricePerApplication ??
            65;
          const incl = Math.round(excl * 1.21 * 100) / 100;
          return (
            <span title={`Partner-prijs (€ ${excl.toFixed(2)} excl. btw)`}>
              {fmtEuro(incl)}
            </span>
          );
        })()}
      </td>
      <td className="px-4 py-2.5 text-right">
        <div
          className="text-xs text-gray-700 tabular-nums"
          title="Ingediend op"
        >
          {row.createdAt
            ? new Date(row.createdAt).toLocaleDateString("nl-NL", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "-"}
        </div>
        {stillDays == null ? null : (
          <span
            title="Dagen sinds laatste activiteit"
            className={`mt-0.5 inline-flex items-center gap-1 text-[11px] ${
              isStuck ? "font-semibold text-rose-600" : "text-gray-400"
            }`}
          >
            <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-12.25a.75.75 0 00-1.5 0V10c0 .2.08.39.22.53l2.25 2.25a.75.75 0 101.06-1.06L10.75 9.7V5.75z"
                clipRule="evenodd"
              />
            </svg>
            {stillDays === 0
              ? "vandaag"
              : stillDays === 1
              ? "gisteren"
              : `${stillDays} dagen`}
          </span>
        )}
      </td>
      <td className="w-10 px-2 py-2.5 text-right">
        <span
          aria-hidden
          className="inline-flex h-7 w-7 items-center justify-center rounded text-gray-300 group-hover:text-gray-600"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M7.3 4.3a1 1 0 011.4 0l5 5a1 1 0 010 1.4l-5 5a1 1 0 11-1.4-1.4L11.6 10 7.3 5.7a1 1 0 010-1.4z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </td>
    </tr>
  );
};

const StatusSection = ({
  status,
  rows,
  selectedIds,
  onToggleRow,
  onToggleSection,
  collapsed,
  onCollapse,
}) => {
  const c = getStatusColor(status.color);
  // Bij partner-aanvragen telt amountInclVat 0 mee (klant betaalt niets); we
  // gebruiken de partner-prijs zodat status-sectie-totalen accuraat blijven.
  const totalAmount = rows.reduce((sum, r) => {
    if (r.partnerUserId) {
      const excl =
        r.partnerUserId?.applicationFees?.btwBatterij ??
        r.partnerUserId?.btwPricePerApplication ??
        65;
      return sum + Math.round(excl * 1.21 * 100) / 100;
    }
    return sum + (r.amountInclVat ?? 0);
  }, 0);
  const allSelected =
    rows.length > 0 && rows.every((r) => selectedIds.has(r._id));
  const someSelected =
    rows.length > 0 && rows.some((r) => selectedIds.has(r._id));

  return (
    <section className="mb-3 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
      <header className="flex items-center gap-3 border-b border-gray-100 px-3 py-2">
        <button
          type="button"
          onClick={onCollapse}
          className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label={collapsed ? "Uitklappen" : "Inklappen"}
        >
          <svg
            className={`h-3.5 w-3.5 transition-transform ${
              collapsed ? "-rotate-90" : ""
            }`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <span className={`h-2 w-2 flex-shrink-0 rounded-full ${c.dot}`} />
        <h3 className="text-sm font-semibold text-gray-900">{status.name}</h3>
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-gray-100 px-1.5 text-[11px] font-medium text-gray-600">
          {rows.length}
        </span>
        {status.isTerminal ? (
          <span className="text-[10px] uppercase tracking-wider text-gray-400">
            eindstation
          </span>
        ) : null}
        <span className="ml-auto text-xs text-gray-500">
          {rows.length > 0 ? (
            <span>
              Totaal{" "}
              <span className="font-medium text-gray-700 tabular-nums">
                {fmtEuro(totalAmount)}
              </span>
            </span>
          ) : null}
        </span>
        {rows.length > 0 ? (
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected && !allSelected;
            }}
            onChange={() => onToggleSection(rows.map((r) => r._id), allSelected)}
            aria-label={`Selecteer alle in ${status.name}`}
            className="ml-2 h-3.5 w-3.5 cursor-pointer rounded border-gray-300"
          />
        ) : null}
      </header>

      {!collapsed ? (
        rows.length === 0 ? (
          <div className="px-6 py-6 text-center text-xs text-gray-400">
            Geen aanvragen in deze status.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <Row
                    key={row._id}
                    row={row}
                    selected={selectedIds.has(row._id)}
                    onToggle={onToggleRow}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </section>
  );
};

const BtwBatterijAanvragenPage = () => {
  const { userData } = useUser();
  const [data, setData] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState({
    totalDocs: 0,
    totalAmount: 0,
    totalAmountPaid: 0,
  });
  // BTW-omzet totaal (betaald + verwacht) uit het dashboard-endpoint. Bewust
  // filter-onafhankelijk en inclusief partner-prijzen, dus consistent met de
  // home- en Financieel-pagina (de header-"Omzet" hierboven volgt wel de filter).
  const [btwRevenue, setBtwRevenue] = useState({ paid: 0, expected: 0 });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [invoiceBuilderOpen, setInvoiceBuilderOpen] = useState(false);
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());
  // Bevestigingsbanner na bulk-actie - zodat de gebruiker niet desoriënteert
  // wanneer items door een actie uit de huidige filter-view verdwijnen.
  const [actionBanner, setActionBanner] = useState(null);
  // shape: { kind: 'archived' | 'unarchived' | 'deleted', count: number }

  // Lees filters uit URL bij mount zodat back-knop / refresh / deeplink ze bewaart.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFromUrl = useMemo(
    () => ({
      search: searchParams.get("search") ?? "",
      workflowStatusId: searchParams.get("workflowStatusId") ?? "",
      assignedToAdminId: searchParams.get("assignedToAdminId") ?? "",
      mine: searchParams.get("mine") === "1",
      unassigned: searchParams.get("unassigned") === "1",
      qualificationStatus: searchParams.get("qualificationStatus") ?? "",
      status: searchParams.get("status") ?? "paid",
      stuck: searchParams.get("stuck") === "1",
      awaitingPartnerPayment: searchParams.get("awaitingPartnerPayment") === "1",
      toInvoice: searchParams.get("toInvoice") === "1",
      labelId: searchParams.get("labelId") ?? "",
      deadline: searchParams.get("deadline") ?? "",
      archived: searchParams.get("archived") ?? "",
    }),
    [],
  );
  const { filters, setFilter, resetFilters } = useBtwFilters(initialFromUrl);
  const debouncedSearch = useDebounced(filters.search, 350);

  // Schrijf filter-state terug naar URL bij elke wijziging - replace zodat we
  // geen history-spam veroorzaken. Browser-back springt nog naar de juiste set.
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.workflowStatusId)
      params.set("workflowStatusId", filters.workflowStatusId);
    if (filters.assignedToAdminId)
      params.set("assignedToAdminId", filters.assignedToAdminId);
    if (filters.mine) params.set("mine", "1");
    if (filters.unassigned) params.set("unassigned", "1");
    if (filters.qualificationStatus)
      params.set("qualificationStatus", filters.qualificationStatus);
    if (filters.status) params.set("status", filters.status);
    if (filters.stuck) params.set("stuck", "1");
    if (filters.awaitingPartnerPayment)
      params.set("awaitingPartnerPayment", "1");
    if (filters.toInvoice) params.set("toInvoice", "1");
    if (filters.labelId) params.set("labelId", filters.labelId);
    if (filters.deadline) params.set("deadline", filters.deadline);
    if (filters.archived) params.set("archived", filters.archived);
    setSearchParams(params, { replace: true });
    const qs = params.toString();
    saveBtwLastUrl(
      "/panel/admin/btw-batterij-overzicht",
      qs ? `?${qs}` : "",
    );
  }, [filters, setSearchParams]);

  useEffect(() => {
    Promise.all([
      adminFetchBtwStatuses(),
      adminFetchAdmins(),
      adminFetchBtwLabels(),
    ])
      .then(([statusRes, adminRes, labelRes]) => {
        setStatuses(statusRes?.data ?? statusRes ?? []);
        setAdmins(adminRes?.data ?? adminRes ?? []);
        setLabels(labelRes?.data ?? labelRes ?? []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSelectedIds(new Set());
    adminFetchBtwApplications({
      page: 1,
      limit: 500,
      search: debouncedSearch,
      // workflowStatusId is purely a UI filter; we still fetch everything and hide sections client-side
      assignedToAdminId: filters.assignedToAdminId,
      mine: filters.mine,
      unassigned: filters.unassigned,
      qualificationStatus: filters.qualificationStatus,
      status: filters.status,
      stuck: filters.stuck,
      awaitingPartnerPayment: filters.awaitingPartnerPayment,
      toInvoice: filters.toInvoice,
      labelId: filters.labelId,
      deadline: filters.deadline,
      archived: filters.archived,
      sort: "activity",
    })
      .then((res) => {
        if (cancelled) return;
        const docs = res?.data?.docs ?? res?.docs ?? [];
        const meta = res?.data ?? res ?? {};
        setData(docs);
        setTotals({
          totalDocs: meta.totalDocs ?? docs.length,
          totalAmount: meta.totalAmount ?? 0,
          totalAmountPaid: meta.totalAmountPaid ?? 0,
        });
      })
      .catch((err) => console.error("Kon BTW aanvragen niet laden", err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    debouncedSearch,
    filters.assignedToAdminId,
    filters.mine,
    filters.unassigned,
    filters.qualificationStatus,
    filters.status,
    filters.stuck,
    // awaitingPartnerPayment ontbrak hier, waardoor dat filter de lijst niet
    // opnieuw ophaalde.
    filters.awaitingPartnerPayment,
    filters.toInvoice,
    filters.labelId,
    filters.deadline,
    filters.archived,
    reloadKey,
  ]);

  // BTW-omzet totaal (betaald + verwacht), filter-onafhankelijk via dashboard.
  // Herlaadt bij reloadKey zodat bulk-acties (bv. partner-factuur) doorwerken.
  useEffect(() => {
    let cancelled = false;
    fetchDashboardData()
      .then((res) => {
        if (cancelled || !res) return;
        setBtwRevenue({
          paid: (res.btwRevenuePaid ?? 0) + (res.partnerInvoicePaidNet ?? 0),
          expected: res.btwRevenueExpected ?? 0,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const visibleStatuses = useMemo(
    () =>
      statuses.filter(
        (s) =>
          !s.archived &&
          (!filters.workflowStatusId || s._id === filters.workflowStatusId),
      ),
    [statuses, filters.workflowStatusId],
  );

  const grouped = useMemo(() => {
    const map = new Map();
    visibleStatuses.forEach((s) => map.set(String(s._id), []));
    (data ?? []).forEach((row) => {
      const sid = String(row.workflowStatusId?._id ?? row.workflowStatusId ?? "");
      if (map.has(sid)) map.get(sid).push(row);
    });
    return map;
  }, [data, visibleStatuses]);

  const showingArchive = filters.archived === "true";

  // Voor de "Maak partner-factuur" knop:
  // Verzameling van geselecteerde rijen die (a) een partnerUserId hebben en
  // (b) nog niet op een verstuurde/betaalde factuur staan. Knop is alleen
  // zinnig als alle geselecteerde rijen dezelfde partner delen, want je
  // factureert per partner.
  const partnerInvoiceCandidate = useMemo(() => {
    if (selectedIds.size === 0) return null;
    const rows = (data ?? []).filter((r) => selectedIds.has(r._id));
    if (rows.length === 0) return null;
    const partnerIds = new Set(
      rows
        .map((r) => r.partnerUserId?._id ?? r.partnerUserId)
        .filter(Boolean)
        .map(String),
    );
    if (partnerIds.size !== 1) return null;
    const partnerUserId = Array.from(partnerIds)[0];
    const partnerNaam =
      rows[0].partnerUserId?.bedrijfsnaam ?? rows[0].partnerUserId?.naam ?? "";
    const blocked = rows.filter((r) => {
      const s = r.partnerInvoice?.status;
      return s === "invoiced" || s === "paid";
    });
    const eligibleIds = rows
      .filter((r) => {
        const s = r.partnerInvoice?.status;
        return !s || s === "pending" || s === "credited";
      })
      .map((r) => r._id);
    return {
      partnerUserId,
      partnerNaam,
      totalSelected: rows.length,
      eligibleIds,
      blockedCount: blocked.length,
    };
  }, [data, selectedIds]);

  const fmtNlDate = (s) =>
    s ? new Date(s).toLocaleDateString("nl-NL") : "";
  const exportCsv = () => {
    const source = (data ?? []).filter(
      (r) => selectedIds.size === 0 || selectedIds.has(r._id),
    );
    if (source.length === 0) return;
    const rows = source.map((r) => ({
      Referentie: String(r._id).slice(-8).toUpperCase(),
      Voorletters: r.step1?.voorletters ?? "",
      Tussenvoegsel: r.step1?.tussenvoegsel ?? "",
      Achternaam: r.step1?.achternaam ?? "",
      Email: r.step1?.email ?? "",
      Telefoon: r.step1?.telefoon ?? "",
      Adres: [
        r.step1?.straatnaam,
        r.step1?.huisnummer,
      ]
        .filter(Boolean)
        .join(" "),
      Postcode: r.step1?.postcode ?? "",
      Plaats: r.step1?.plaatsnaam ?? "",
      Kwalificatie: r.qualificationStatus ?? "",
      WorkflowStatus: r.workflowStatusId?.name ?? "",
      Toegewezen: r.assignedToAdminId?.naam ?? "",
      Betaalstatus: r.status ?? "",
      Partner: r.partnerUserId?.bedrijfsnaam ?? r.partnerUserId?.naam ?? "",
      Partnerfactuur: r.partnerInvoice?.invoiceId?.invoiceNumber ?? "",
      PartnerfactuurStatus: r.partnerUserId ? (r.partnerInvoice?.status ?? "pending") : "",
      BedragInclBtw: (r.amountInclVat ?? 0).toFixed(2),
      Factuurdatum: fmtNlDate(r.step3?.invoiceDate),
      Installatiedatum: fmtNlDate(r.step3?.installationDate),
      CapaciteitKwh: r.step3?.batteryCapacityKwh ?? "",
      IngediendOp: fmtNlDate(r.createdAt),
      LaatsteActiviteit: fmtNlDate(r.lastActivityAt),
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const datePart = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `btw-aanvragen-${datePart}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSection = (ids, allCurrentlySelected) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allCurrentlySelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleCollapse = (statusId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(statusId)) next.delete(statusId);
      else next.add(statusId);
      return next;
    });
  };

  const runBulkMoveStatus = async (statusId, statusName) => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    setBulkBusy(true);
    setMoveMenuOpen(false);
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          adminUpdateBtwWorkflowStatus(id, statusId).catch((e) =>
            console.error(`Verplaatsen mislukt voor ${id}`, e),
          ),
        ),
      );
      setSelectedIds(new Set());
      setReloadKey((k) => k + 1);
      setActionBanner({ kind: "moved", count, statusName });
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkArchive = async () => {
    if (selectedIds.size === 0) return;
    const archive = !showingArchive;
    const count = selectedIds.size;
    setBulkBusy(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          adminArchiveBtwApplication(id, archive).catch((e) =>
            console.error(`Mislukt voor ${id}`, e),
          ),
        ),
      );
      setSelectedIds(new Set());
      setReloadKey((k) => k + 1);
      setActionBanner({ kind: archive ? "archived" : "unarchived", count });
    } finally {
      setBulkBusy(false);
      setConfirmOpen(false);
    }
  };

  const runBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    setBulkBusy(true);
    const failed = [];
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          adminDeleteBtwApplication(id).catch((e) => {
            console.error(`Verwijderen mislukt voor ${id}`, e);
            failed.push(id);
          }),
        ),
      );
      setSelectedIds(new Set());
      setReloadKey((k) => k + 1);
      setActionBanner({
        kind: "deleted",
        count: count - failed.length,
        failed: failed.length,
      });
    } finally {
      setBulkBusy(false);
      setDeleteOpen(false);
    }
  };

  // Auto-dismiss banner na 8 sec
  useEffect(() => {
    if (!actionBanner) return undefined;
    const t = setTimeout(() => setActionBanner(null), 8000);
    return () => clearTimeout(t);
  }, [actionBanner]);

  return (
    <div className="page-wrapper px-6 pt-24 pb-12">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">
            BTW Batterij
          </h2>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
            <span>
              <span className="font-medium text-gray-900">{totals.totalDocs}</span>{" "}
              aanvragen
            </span>
            <span aria-hidden>·</span>
            <span>
              Omzet{" "}
              <span className="font-medium text-emerald-700 tabular-nums">
                {fmtEuro(totals.totalAmountPaid)}
              </span>
            </span>
          </div>
          {/* BTW-totaal betaald + verwacht (alle billing-modes, filter-onafhankelijk) */}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="text-gray-500">
              BTW betaald{" "}
              <span className="font-medium text-emerald-700 tabular-nums">
                {fmtEuro(btwRevenue.paid)}
              </span>
            </span>
            <span aria-hidden className="text-gray-400">·</span>
            <span className="text-gray-500">
              Verwacht{" "}
              <span className="font-medium text-amber-700 tabular-nums">
                {fmtEuro(btwRevenue.expected)}
              </span>
            </span>
            <span aria-hidden className="text-gray-400">·</span>
            <span className="text-gray-500">
              Totaal{" "}
              <span className="font-semibold text-gray-900 tabular-nums">
                {fmtEuro(btwRevenue.paid + btwRevenue.expected)}
              </span>
            </span>
          </div>
        </div>
      </div>

      <BtwFilterBar
        view="list"
        listTo="/panel/admin/btw-batterij-overzicht"
        boardTo="/panel/admin/btw-batterij-board"
        filters={filters}
        setFilter={setFilter}
        resetFilters={resetFilters}
        statuses={statuses}
        admins={admins}
        labels={labels}
        currentUserId={userData?._id}
      />

      {actionBanner ? (
        <div
          className={`mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm ring-1 ${
            actionBanner.kind === "deleted"
              ? "bg-rose-50 text-rose-900 ring-rose-200"
              : "bg-emerald-50 text-emerald-900 ring-emerald-200"
          }`}
        >
          <span>
            {actionBanner.kind === "archived" ? (
              <>
                <strong>{actionBanner.count}</strong> aanvraag/aanvragen gearchiveerd.
              </>
            ) : actionBanner.kind === "unarchived" ? (
              <>
                <strong>{actionBanner.count}</strong> aanvraag/aanvragen teruggehaald uit archief.
              </>
            ) : actionBanner.kind === "moved" ? (
              <>
                <strong>{actionBanner.count}</strong> aanvraag/aanvragen verplaatst naar <strong>{actionBanner.statusName}</strong>.
              </>
            ) : actionBanner.kind === "invoice_sent" ? (
              <>
                Factuur <strong>{actionBanner.invoiceNumber}</strong> aangemaakt en verstuurd ({actionBanner.count} regels).
              </>
            ) : actionBanner.kind === "invoice_draft" ? (
              <>
                Factuur <strong>{actionBanner.invoiceNumber}</strong> opgeslagen als draft ({actionBanner.count} regels).
              </>
            ) : (
              <>
                <strong>{actionBanner.count}</strong> aanvraag/aanvragen definitief verwijderd
                {actionBanner.failed
                  ? `. ${actionBanner.failed} mislukt.`
                  : "."}
              </>
            )}
          </span>
          <div className="flex items-center gap-2">
            {actionBanner.kind === "archived" ? (
              <button
                type="button"
                onClick={() => setFilter("archived", "true")}
                className="rounded-md border border-emerald-300 px-2.5 py-1 text-xs font-medium hover:bg-emerald-100"
              >
                Toon archief
              </button>
            ) : null}
            {actionBanner.kind === "unarchived" ? (
              <button
                type="button"
                onClick={() => setFilter("archived", "")}
                className="rounded-md border border-emerald-300 px-2.5 py-1 text-xs font-medium hover:bg-emerald-100"
              >
                Toon actief
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setActionBanner(null)}
              className="rounded p-1 text-current/60 hover:bg-black/5"
              aria-label="Sluiten"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      ) : null}

      {selectedIds.size > 0 ? (
        <div className="sticky top-[88px] z-10 mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gray-900 px-3 py-2 text-sm text-white shadow-md">
          <span className="font-medium">{selectedIds.size} geselecteerd</span>
          <div className="flex gap-2">
            <button
              onClick={exportCsv}
              disabled={bulkBusy}
              className="rounded-md border border-white/20 px-2.5 py-1 text-white hover:bg-white/10 disabled:opacity-50"
            >
              Exporteer ({selectedIds.size})
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setMoveMenuOpen((v) => !v)}
                disabled={bulkBusy || visibleStatuses.length === 0}
                className="rounded-md border border-white/20 px-2.5 py-1 text-white hover:bg-white/10 disabled:opacity-50"
              >
                Verplaats naar ({selectedIds.size})
                <svg
                  className="ml-1 inline h-3 w-3"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.3 7.3a1 1 0 011.4 0L10 10.6l3.3-3.3a1 1 0 111.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {moveMenuOpen ? (
                <>
                  {/* backdrop sluit menu */}
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setMoveMenuOpen(false)}
                  />
                  <div className="absolute right-0 z-40 mt-1 w-56 rounded-md bg-white p-1 text-sm text-gray-900 shadow-lg ring-1 ring-gray-200">
                    <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      Verplaats naar workflow-status
                    </p>
                    {visibleStatuses.map((s) => (
                      <button
                        key={s._id}
                        type="button"
                        onClick={() => runBulkMoveStatus(s._id, s.name)}
                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-gray-100"
                      >
                        <span
                          className={`h-2 w-2 rounded-full ${getStatusColor(s.color).dot}`}
                        />
                        <span>{s.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
            {partnerInvoiceCandidate &&
            partnerInvoiceCandidate.eligibleIds.length > 0 ? (
              <button
                onClick={() => setInvoiceBuilderOpen(true)}
                disabled={bulkBusy}
                className="rounded-md bg-mint-600 px-2.5 py-1 font-medium text-white hover:bg-mint-700 disabled:opacity-50"
                title={
                  partnerInvoiceCandidate.blockedCount > 0
                    ? `${partnerInvoiceCandidate.blockedCount} aanvraag/aanvragen staan al op een verstuurde factuur en worden overgeslagen.`
                    : ""
                }
              >
                Maak partner-factuur ({partnerInvoiceCandidate.eligibleIds.length})
              </button>
            ) : null}
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={bulkBusy}
              className="rounded-md bg-white px-2.5 py-1 font-medium text-gray-900 hover:bg-gray-100 disabled:opacity-50"
            >
              {showingArchive
                ? `Uit archief halen (${selectedIds.size})`
                : `Archiveren (${selectedIds.size})`}
            </button>
            <button
              onClick={() => setDeleteOpen(true)}
              disabled={bulkBusy}
              className="rounded-md bg-rose-600 px-2.5 py-1 font-medium text-white hover:bg-rose-700 disabled:opacity-50"
            >
              Verwijder ({selectedIds.size})
            </button>
          </div>
        </div>
      ) : (data ?? []).length > 0 ? (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM9 3a1 1 0 011 1v8.586l2.293-2.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V4a1 1 0 011-1z" />
            </svg>
            Exporteer CSV ({(data ?? []).length})
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-xl bg-white p-16 text-center text-sm text-gray-500 shadow-sm ring-1 ring-gray-200">
          Laden…
        </div>
      ) : visibleStatuses.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 p-12 text-center text-sm text-gray-500">
          Geen statussen geconfigureerd. Maak ze aan via &laquo;Statussen beheren&raquo;.
        </div>
      ) : (
        visibleStatuses.map((s) => (
          <StatusSection
            key={s._id}
            status={s}
            rows={grouped.get(String(s._id)) ?? []}
            selectedIds={selectedIds}
            onToggleRow={toggleRow}
            onToggleSection={toggleSection}
            collapsed={collapsed.has(s._id)}
            onCollapse={() => toggleCollapse(s._id)}
          />
        ))
      )}

      <ConfirmModal
        isOpen={confirmOpen}
        title={showingArchive ? "Uit archief halen?" : "Aanvragen archiveren?"}
        message={
          showingArchive
            ? `${selectedIds.size} aanvraag/aanvragen worden teruggezet naar het actieve overzicht.`
            : `${selectedIds.size} aanvraag/aanvragen worden gearchiveerd en verdwijnen uit het actieve overzicht. Je kunt ze later terughalen door de Archief-toggle aan te zetten.`
        }
        confirmLabel={
          showingArchive
            ? `Uit archief halen (${selectedIds.size})`
            : `Archiveren (${selectedIds.size})`
        }
        cancelLabel="Annuleren"
        busy={bulkBusy}
        onConfirm={runBulkArchive}
        onCancel={() => setConfirmOpen(false)}
      />

      <ConfirmModal
        isOpen={deleteOpen}
        variant="danger"
        title={`${selectedIds.size} aanvraag/aanvragen definitief verwijderen?`}
        message="Deze aanvragen verdwijnen volledig uit de admin en zijn niet meer terug te halen via de interface. Stripe-betalingen blijven beschikbaar in het Stripe Dashboard."
        confirmLabel={`Verwijder definitief (${selectedIds.size})`}
        cancelLabel="Annuleren"
        busy={bulkBusy}
        onConfirm={runBulkDelete}
        onCancel={() => setDeleteOpen(false)}
      />

      {invoiceBuilderOpen && partnerInvoiceCandidate ? (
        <PartnerInvoiceBuilderModal
          isOpen={invoiceBuilderOpen}
          onClose={() => setInvoiceBuilderOpen(false)}
          onCreated={(invoice, { sent }) => {
            setInvoiceBuilderOpen(false);
            setSelectedIds(new Set());
            setReloadKey((k) => k + 1);
            setActionBanner({
              kind: sent ? "invoice_sent" : "invoice_draft",
              count: invoice?.lines?.length ?? 0,
              invoiceNumber: invoice?.invoiceNumber,
            });
          }}
          partnerUserId={partnerInvoiceCandidate.partnerUserId}
          applicationIds={partnerInvoiceCandidate.eligibleIds}
        />
      ) : null}
    </div>
  );
};

export default BtwBatterijAanvragenPage;
